import type { Booking, BookingType } from '@coachapp/shared';

/** Draft preserved when opening a nested form (e.g. plan creation from booking). */
export interface BookingFormDraft {
  clientId?: string;
  trainingPlanId?: string;
  startsAt?: string;
  type?: BookingType;
  durationMinutes?: number;
  link?: string;
}

export interface BookingFormLocationState {
  booking?: Booking;
  startsAt?: string;
  clientId?: string;
  trainingPlanId?: string;
  type?: BookingType;
  durationMinutes?: number;
  link?: string;
}

export interface PlanFormReturnState {
  returnTo?: string;
  returnState?: BookingFormDraft;
  booking?: Booking;
  /** Pre-fill the new plan form from an existing plan (copy without persisting). */
  copyFromPlanId?: string;
}

export type BookingWithPlan = Booking & {
  trainingPlan?: { id: string; name: string } | null;
};

const BOOKING_DRAFT_KEY = 'coachapp:booking-form-draft';

export function saveBookingFormDraft(draft: BookingFormDraft) {
  try {
    sessionStorage.setItem(BOOKING_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* quota / private mode */
  }
}

export function peekBookingFormDraft(): BookingFormDraft | null {
  try {
    const raw = sessionStorage.getItem(BOOKING_DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as BookingFormDraft;
  } catch {
    return null;
  }
}

export function clearBookingFormDraft() {
  try {
    sessionStorage.removeItem(BOOKING_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

export function mergeBookingDrafts(
  ...sources: (BookingFormDraft | null | undefined)[]
): BookingFormDraft | undefined {
  const merged: BookingFormDraft = {};

  for (const source of sources) {
    if (!source) continue;
    if (source.clientId) merged.clientId = source.clientId;
    if (source.trainingPlanId) merged.trainingPlanId = source.trainingPlanId;
    if (source.startsAt) merged.startsAt = source.startsAt;
    if (source.type) merged.type = source.type;
    if (source.durationMinutes != null) merged.durationMinutes = source.durationMinutes;
    if (source.link) merged.link = source.link;
  }

  return Object.keys(merged).length > 0 ? merged : undefined;
}

export function draftFromLocationState(
  state: BookingFormLocationState | null,
): BookingFormDraft | undefined {
  if (!state) return undefined;

  return mergeBookingDrafts({
    clientId: state.clientId,
    trainingPlanId: state.trainingPlanId,
    startsAt: state.startsAt,
    type: state.type,
    durationMinutes: state.durationMinutes,
    link: state.link,
  });
}
